import { type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import type { ScopeVerdict } from '../lib/scope-gate';
import type { SystemPrompt } from '../lib/system-prompts';
import type { PromptEditorState } from '../lib/use-prompt-editor';
import { AgentModesPanel } from './AgentModesPanel';
import { EmptyState } from './EmptyState';
import { PageHeader } from './PageHeader';
import { QueryBoundary, type QueryLike } from './QueryBoundary';
import { Skeleton } from './Skeleton';
import { SystemPromptEditor } from './SystemPromptEditor';

// Presenter for the Prompts route (#637) — mock `Prompts`: PageHeader (no
// actions) over an xl:3 grid of prompt cards. Only the ORG + USER layers render
// here; the REPO layer lives on the repo settings tab, so its grid slot stays
// empty. Pure rendering; `SystemPrompts` owns the state.
//
// Each layer carries TWO independent halves: the fetch (`orgQuery`/`mineQuery`,
// wrapped in its own `QueryBoundary`) and the save/clear mutation state
// (`org`/`mine`, rendered by `SystemPromptEditor` itself). Conflating them would
// either paint an empty textarea while the initial GET is still in flight, or
// swallow a failed GET entirely — this was the one data view in the app with
// no loading/error/retry for its own read.
//
// Under the prose, the org's agent modes (plan 101 slice 06) — the other half of
// "how this account's agents run": which modes run at all and with which tools.
// A self-fetching section, so it takes the token directly rather than through
// this view's own query props.
export type SystemPromptsViewProps = {
  readonly hasSession: boolean;
  readonly token: string | null;
  /** `write:prompts` per `/v1/me`: `denied` swaps the org editor for the reason (#7426). */
  readonly orgVerdict: ScopeVerdict;
  readonly orgQuery: QueryLike<SystemPrompt | null>;
  readonly org: PromptEditorState;
  readonly mineQuery: QueryLike<SystemPrompt | null>;
  readonly mine: PromptEditorState;
};

export function SystemPromptsView(props: SystemPromptsViewProps): JSX.Element {
  return (
    <>
      <PageHeader title={t('system_prompts.title')} desc={t('system_prompts.cascade')} />

      <Show
        when={props.hasSession}
        fallback={<EmptyState message={t('system_prompts.no_session')} />}
      >
        <div class="prompts-grid">
          <Switch fallback={<Skeleton class="skeleton--card" />}>
            <Match when={props.orgVerdict === 'denied'}>
              <section class="card prompt-card" aria-labelledby="org-prompt-denied">
                <h2 class="card__title" id="org-prompt-denied">
                  {t('system_prompts.org_title')}
                </h2>
                <p class="list-empty" role="status">
                  {t('system_prompts.org_denied')}
                </p>
              </section>
            </Match>
            <Match when={props.orgVerdict === 'allowed'}>
              <QueryBoundary query={props.orgQuery} loading={<Skeleton class="skeleton--card" />}>
                {() => (
                  <SystemPromptEditor
                    id="org-prompt"
                    title={t('system_prompts.org_title')}
                    description={t('system_prompts.org_description')}
                    value={props.org.value()}
                    draft={props.org.draft()}
                    onDraft={props.org.onDraft}
                    role={props.org.role()}
                    onRoleChange={props.org.onRoleChange}
                    busy={props.org.busy()}
                    error={props.org.error()}
                    savedText={props.org.savedText()}
                    onSave={props.org.onSave}
                    onClear={props.org.onClear}
                  />
                )}
              </QueryBoundary>
            </Match>
          </Switch>

          <QueryBoundary query={props.mineQuery} loading={<Skeleton class="skeleton--card" />}>
            {() => (
              <SystemPromptEditor
                id="user-prompt"
                title={t('system_prompts.user_title')}
                description={t('system_prompts.user_description')}
                value={props.mine.value()}
                draft={props.mine.draft()}
                onDraft={props.mine.onDraft}
                role={props.mine.role()}
                onRoleChange={props.mine.onRoleChange}
                busy={props.mine.busy()}
                error={props.mine.error()}
                savedText={props.mine.savedText()}
                onSave={props.mine.onSave}
                onClear={props.mine.onClear}
              />
            )}
          </QueryBoundary>
        </div>
        <AgentModesPanel scope={{ kind: 'org' }} token={props.token} />
      </Show>
    </>
  );
}
